/**
 * Synthetic round index for DEMO mode (no `VITE_API_BASE_URL`). The same four
 * expedientes the demo detail serves (screens/detail/demoDetail.ts), so a
 * click-through from the browser lands on a populated detail. Decision,
 * adjudication and motivos match the rows the paged demo used to show; names,
 * CURPs and the rest of the extraction feed `search_text` so the search bar
 * works without a backend (try «ramirez», «demo0101» or «menor de edad»).
 */

import type { ExpedienteIndexRow } from "../../api/client";
import { demoExpedienteDetail } from "../detail/demoDetail";
import { fold } from "./search";

// Tags are pinned here (not taken from the demo detail) so the table looks
// exactly like the paged demo it replaces.
type DemoRow = Pick<ExpedienteIndexRow, "expediente_id" | "decision" | "adjudicated" | "tags">;

const DEMO_ROWS: DemoRow[] = [
  { expediente_id: "EXP-DEMO-0001", decision: "HUMAN_REVIEW", adjudicated: false, tags: ["menor_de_edad"] },
  // Adjudicated example: a human approved a previously-flagged expediente.
  { expediente_id: "EXP-DEMO-0002", decision: "APPROVED", adjudicated: true, tags: [] },
  { expediente_id: "EXP-DEMO-0003", decision: "APPROVED", adjudicated: false, tags: [] },
  // Single identification file filling both INE slots (ADR 0007 / #275).
  { expediente_id: "EXP-DEMO-0004", decision: "HUMAN_REVIEW", adjudicated: false, tags: [] },
];

function nameOf(fields: Record<string, unknown>): string | null {
  for (const source of ["ine_front", "certificado"]) {
    const doc = fields[source] as Record<string, unknown> | null | undefined;
    const nombre = doc?.nombre as { value?: unknown } | string | undefined;
    const value = typeof nombre === "string" ? nombre : nombre?.value;
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function demoRow({ expediente_id, decision, adjudicated, tags }: DemoRow): ExpedienteIndexRow {
  const automated = demoExpedienteDetail(expediente_id)?.automated;
  const fields = (automated?.extracted_fields ?? {}) as Record<string, unknown>;
  return {
    expediente_id,
    display_name: nameOf(fields),
    decision,
    adjudicated,
    priority: automated?.priority ?? null,
    failures: automated?.failures ?? [],
    flags: automated?.flags ?? [],
    tags,
    search_text: buildSearchText(expediente_id, fields, tags),
  };
}

/** Every text leaf (strings and numbers, depth-first; never booleans, nulls or
 * keys) — `_text_leaves` in src/vera/api/expediente_index.py. */
function textLeaves(value: unknown, out: string[]): string[] {
  if (typeof value === "string") out.push(value);
  else if (typeof value === "number") out.push(String(value));
  else if (Array.isArray(value)) value.forEach((v) => textLeaves(v, out));
  else if (value && typeof value === "object")
    Object.values(value).forEach((v) => textLeaves(v, out));
  return out;
}

/** The server's `build_search_text`, so DEMO search behaves like the real one. */
function buildSearchText(
  id: string,
  fields: Record<string, unknown>,
  tags: string[],
): string {
  const parts = [id, ...textLeaves(fields, []), ...tags.map((t) => t.replace(/_/g, " "))];
  return fold(parts.join(" ")).split(/\s+/).filter(Boolean).join(" ");
}

export const DEMO_INDEX: ExpedienteIndexRow[] = DEMO_ROWS.map(demoRow);
